import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Tag, Calendar, Clock } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { AnimatedSection } from '../components/AnimatedSection';
import { articles } from '../data/articles';
export function ResourcesPage() {
  const [activeTab, setActiveTab] = useState<'resources' | 'blog'>('resources');
  const resourcePosts = articles.filter(a => a.type === 'resource');
  const blogPosts = articles.filter(a => a.type === 'blog');
  return <div className="w-full bg-warm-white" data-id="element-648">
      {/* A) HERO */}
      <div className="bg-primary-900 text-warm-white py-20 lg:py-28" data-id="element-649">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-650">
          <h1 className="text-4xl lg:text-6xl font-heading mb-6" data-id="element-651">
            Learn & Explore
          </h1>
          <p className="text-xl text-primary-100 font-body max-w-2xl mx-auto" data-id="element-652">
            Discover free emergency guides, practical tips, and stories from the
            field to help you become a more prepared pet parent.
          </p>
        </div>
      </div>

      {/* B) TAB NAVIGATION */}
      <div className="bg-warm-white border-b border-warm-gray-200 sticky top-[72px] z-40" data-id="element-653">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-654">
          <div className="flex justify-center gap-8" data-id="element-655">
            <button onClick={() => setActiveTab('resources')} className={`relative py-4 px-2 font-heading text-lg transition-colors ${activeTab === 'resources' ? 'text-primary-900' : 'text-warm-gray-500 hover:text-primary-700'}`} data-id="element-656">
              Free Resources
              {activeTab === 'resources' && <motion.div layoutId="activeTab" className="absolute bottom-0 left-0 right-0 h-0.5 bg-accent-500" initial={false} transition={{
              type: 'spring',
              stiffness: 500,
              damping: 30
            }} data-id="element-657" />}
            </button>
            <button onClick={() => setActiveTab('blog')} className={`relative py-4 px-2 font-heading text-lg transition-colors ${activeTab === 'blog' ? 'text-primary-900' : 'text-warm-gray-500 hover:text-primary-700'}`} data-id="element-658">
              Blog
              {activeTab === 'blog' && <motion.div layoutId="activeTab" className="absolute bottom-0 left-0 right-0 h-0.5 bg-accent-500" initial={false} transition={{
              type: 'spring',
              stiffness: 500,
              damping: 30
            }} data-id="element-659" />}
            </button>
          </div>
        </div>
      </div>

      {/* C) TAB CONTENT */}
      <div className="min-h-[600px]" data-id="element-660">
        <AnimatePresence mode="wait" data-id="element-661">
          {activeTab === 'resources' && <motion.div key="resources" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -20
        }} transition={{
          duration: 0.3
        }} data-id="element-662">
              {/* FEATURED CONTENT */}
              <AnimatedSection className="py-16 bg-warm-white" data-id="element-663">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-664">
                  <div className="text-center max-w-3xl mx-auto mb-12" data-id="element-665">
                    <h2 className="text-3xl lg:text-4xl font-heading text-primary-900 mb-4" data-id="element-666">
                      Essential Skills to Start With
                    </h2>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-667">
                    {resourcePosts.slice(0, 3).map((post, i) => <Link to={`/resources/${post.slug}`} key={i} className="bg-white rounded-2xl overflow-hidden shadow-warm hover:shadow-warm-lg transition-all border border-warm-gray-100 flex flex-col h-full group" data-id="element-668">
                        <div className="relative h-56 overflow-hidden" data-id="element-669">
                          <img src={post.img} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-670" />
                          <div className="absolute top-4 left-4 bg-white/90 backdrop-blur px-3 py-1 rounded-full text-xs font-bold font-body text-primary-800 flex items-center gap-1" data-id="element-671">
                            <Tag className="w-3 h-3" data-id="element-672" /> {post.cat}
                          </div>
                        </div>
                        <div className="p-6 flex flex-col flex-grow" data-id="element-673">
                          <div className="flex items-center gap-2 text-warm-gray-500 text-xs font-body mb-3" data-id="element-674">
                            <Clock className="w-3 h-3" data-id="element-675" />
                            <span data-id="element-676">{post.readTime}</span>
                          </div>
                          <h2 className="font-heading text-2xl text-primary-900 mb-3 group-hover:text-accent-600 transition-colors" data-id="element-677">
                            {post.title}
                          </h2>
                          <p className="font-body text-warm-gray-600 text-sm leading-relaxed mb-6 flex-grow" data-id="element-678">
                            {post.excerpt}
                          </p>
                          <span className="inline-flex items-center gap-2 text-primary-800 font-medium font-body group-hover:text-accent-600 transition-colors mt-auto" data-id="element-679">
                            Start Learning <ArrowRight className="w-4 h-4" data-id="element-680" />
                          </span>
                        </div>
                      </Link>)}
                  </div>
                </div>
              </AnimatedSection>

              {/* ALL RESOURCES */}
              <AnimatedSection className="py-16 bg-warm-gray-50" data-id="element-681">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-682">
                  <div className="text-center max-w-3xl mx-auto mb-12" data-id="element-683">
                    <h2 className="text-3xl lg:text-4xl font-heading text-primary-900 mb-4" data-id="element-684">
                      All Free Resources
                    </h2>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-685">
                    {resourcePosts.map((post, i) => <Link to={`/resources/${post.slug}`} key={i} className="bg-white rounded-2xl overflow-hidden shadow-warm hover:shadow-warm-lg transition-all border border-warm-gray-100 flex flex-col h-full group" data-id="element-686">
                        <div className="relative h-56 overflow-hidden" data-id="element-687">
                          <img src={post.img} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-688" />
                          <div className="absolute top-4 left-4 bg-white/90 backdrop-blur px-3 py-1 rounded-full text-xs font-bold font-body text-primary-800 flex items-center gap-1" data-id="element-689">
                            <Tag className="w-3 h-3" data-id="element-690" /> {post.cat}
                          </div>
                        </div>
                        <div className="p-6 flex flex-col flex-grow" data-id="element-691">
                          <div className="flex items-center gap-2 text-warm-gray-500 text-xs font-body mb-3" data-id="element-692">
                            <Clock className="w-3 h-3" data-id="element-693" />
                            <span data-id="element-694">{post.readTime}</span>
                          </div>
                          <h2 className="font-heading text-2xl text-primary-900 mb-3 group-hover:text-accent-600 transition-colors" data-id="element-695">
                            {post.title}
                          </h2>
                          <p className="font-body text-warm-gray-600 text-sm leading-relaxed mb-6 flex-grow" data-id="element-696">
                            {post.excerpt}
                          </p>
                          <span className="inline-flex items-center gap-2 text-primary-800 font-medium font-body group-hover:text-accent-600 transition-colors mt-auto" data-id="element-697">
                            Start Learning <ArrowRight className="w-4 h-4" data-id="element-698" />
                          </span>
                        </div>
                      </Link>)}
                  </div>
                </div>
              </AnimatedSection>
            </motion.div>}

          {activeTab === 'blog' && <motion.div key="blog" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -20
        }} transition={{
          duration: 0.3
        }} data-id="element-699">
              <AnimatedSection className="py-16 bg-warm-white" data-id="element-700">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-701">
                  <div className="text-center max-w-3xl mx-auto mb-12" data-id="element-702">
                    <h2 className="text-3xl lg:text-4xl font-heading text-primary-900 mb-4" data-id="element-703">
                      Latest from the Blog
                    </h2>
                    <p className="text-warm-gray-600 font-body text-lg" data-id="element-704">
                      Stories, insights, and behind-the-scenes looks at pet
                      safety training.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-705">
                    {blogPosts.map((post, i) => <Link to={`/resources/${post.slug}`} key={i} className="bg-white rounded-2xl overflow-hidden shadow-warm hover:shadow-warm-lg transition-all border border-warm-gray-100 flex flex-col h-full group" data-id="element-706">
                        <div className="relative h-56 overflow-hidden" data-id="element-707">
                          <img src={post.img} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-708" />
                          <div className="absolute top-4 left-4 bg-white/90 backdrop-blur px-3 py-1 rounded-full text-xs font-bold font-body text-primary-800 flex items-center gap-1" data-id="element-709">
                            <Tag className="w-3 h-3" data-id="element-710" /> {post.cat}
                          </div>
                        </div>
                        <div className="p-6 flex flex-col flex-grow" data-id="element-711">
                          <div className="flex items-center gap-4 text-warm-gray-500 text-xs font-body mb-3" data-id="element-712">
                            <div className="flex items-center gap-1" data-id="element-713">
                              <Calendar className="w-3 h-3" data-id="element-714" />
                              <span data-id="element-715">{post.date}</span>
                            </div>
                            <div className="flex items-center gap-1" data-id="element-716">
                              <Clock className="w-3 h-3" data-id="element-717" />
                              <span data-id="element-718">{post.readTime}</span>
                            </div>
                          </div>
                          <h2 className="font-heading text-2xl text-primary-900 mb-3 group-hover:text-accent-600 transition-colors" data-id="element-719">
                            {post.title}
                          </h2>
                          <p className="font-body text-warm-gray-600 text-sm leading-relaxed mb-6 flex-grow" data-id="element-720">
                            {post.excerpt}
                          </p>
                          <span className="inline-flex items-center gap-2 text-primary-800 font-medium font-body group-hover:text-accent-600 transition-colors mt-auto" data-id="element-721">
                            Read More <ArrowRight className="w-4 h-4" data-id="element-722" />
                          </span>
                        </div>
                      </Link>)}
                  </div>
                </div>
              </AnimatedSection>
            </motion.div>}
        </AnimatePresence>
      </div>

      {/* D) UPGRADE BRIDGE */}
      <AnimatedSection className="py-24 bg-primary-50" data-id="element-723">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-724">
          <h2 className="text-3xl font-heading text-primary-900 mb-4" data-id="element-725">
            Ready to Go Deeper?
          </h2>
          <p className="text-lg text-warm-gray-600 font-body mb-8" data-id="element-726">
            Free resources are a great start. Our structured video course takes
            you from basics to confident responder.
          </p>
          <div className="flex flex-col sm:flex-row justify-center items-center gap-4" data-id="element-727">
            <Link to="/video-course" className="inline-flex justify-center items-center px-8 py-4 border-2 border-primary-800 text-base font-bold rounded-full text-primary-800 bg-transparent hover:bg-primary-100 transition-all" data-id="element-728">
              View Video Course
            </Link>
            <Link to="/programs" className="inline-flex items-center gap-2 text-primary-800 font-bold font-body hover:text-accent-600 transition-colors px-4 py-4" data-id="element-729">
              Explore All Programs <ArrowRight className="w-4 h-4" data-id="element-730" />
            </Link>
          </div>
        </div>
      </AnimatedSection>
    </div>;
}